import React, { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Mic,
  MicOff,
  Send,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Check,
  ArrowRight,
  User,
  Mail,
  Sparkles,
  MessageCircle,
  CheckCircle,
  Circle,
  Clock,
  Shield,
  Heart,
  Zap,
  Eye,
  Battery,
  Target,
  UserCheck,
  Flag,
  ChevronDown,
  Calendar,
  Briefcase,
  MessageSquare,
} from "lucide-react";

import "./App.css";
import MyPurposeProfileLanding from "./MyPurposeProfileLanding";
import DonePage from "./DonePage";

// Modern CSS with animations
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
    /* Custom font for titles and questions */
  @font-face {
    font-family: 'P22MackinacPro';
    src: url('/P22MackinacPro-Medium_26.otf') format('opentype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
`;

// Modern styled components
const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Helper function to get categories from translations
const getCategories = (t) => {
  const categoryMap = {
    "life-vision": { icon: Eye, color: "purple", startIndex: 0, endIndex: 5 },
    "energy-stress": { icon: Battery, color: "blue", startIndex: 6, endIndex: 10 },
    "fulfillment-values": { icon: Target, color: "green", startIndex: 11, endIndex: 15 },
    "self-image": { icon: UserCheck, color: "orange", startIndex: 16, endIndex: 22 },
    "needs-nonnegotiables": { icon: Flag, color: "red", startIndex: 23, endIndex: 24 },
  };

  return [
    {
      id: "life-vision",
      name: t("assessment.categories.lifeVision.name"),
      shortName: t("assessment.categories.lifeVision.shortName"),
      icon: Eye,
      color: "purple",
      startIndex: 0,
      endIndex: 5,
      description: t("assessment.categories.lifeVision.description"),
    },
    {
      id: "energy-stress",
      name: t("assessment.categories.energyStress.name"),
      shortName: t("assessment.categories.energyStress.shortName"),
      icon: Battery,
      color: "blue",
      startIndex: 6,
      endIndex: 10,
      description: t("assessment.categories.energyStress.description"),
    },
    {
      id: "fulfillment-values",
      name: t("assessment.categories.fulfillmentValues.name"),
      shortName: t("assessment.categories.fulfillmentValues.shortName"),
      icon: Target,
      color: "green",
      startIndex: 11,
      endIndex: 15,
      description: t("assessment.categories.fulfillmentValues.description"),
    },
    {
      id: "self-image",
      name: t("assessment.categories.selfImage.name"),
      shortName: t("assessment.categories.selfImage.shortName"),
      icon: UserCheck,
      color: "orange",
      startIndex: 16,
      endIndex: 22,
      description: t("assessment.categories.selfImage.description"),
    },
    {
      id: "needs-nonnegotiables",
      name: t("assessment.categories.needsNonnegotiables.name"),
      shortName: t("assessment.categories.needsNonnegotiables.shortName"),
      icon: Flag,
      color: "red",
      startIndex: 23,
      endIndex: 24,
      description: t("assessment.categories.needsNonnegotiables.description"),
    },
  ];
};

// Helper function to get questions from translations
const getQuestionsConfig = (t) => {
  const lifeVision = t("assessment.questionsList.lifeVision", { returnObjects: true });
  const energyStress = t("assessment.questionsList.energyStress", { returnObjects: true });
  const fulfillmentValues = t("assessment.questionsList.fulfillmentValues", { returnObjects: true });
  const selfImage = t("assessment.questionsList.selfImage", { returnObjects: true });
  const needsNonnegotiables = t("assessment.questionsList.needsNonnegotiables", { returnObjects: true });

  let id = 1;
  const questions = [];

  // Life Vision (1-6)
  lifeVision.forEach((question) => {
    questions.push({ id: id++, categoryId: "life-vision", question });
  });

  // Energy, Stress (7-11)
  energyStress.forEach((question) => {
    questions.push({ id: id++, categoryId: "energy-stress", question });
  });

  // Fulfillment, Values (12-16)
  fulfillmentValues.forEach((question) => {
    questions.push({ id: id++, categoryId: "fulfillment-values", question });
  });

  // Self-Image (17-23)
  selfImage.forEach((question) => {
    questions.push({ id: id++, categoryId: "self-image", question });
  });

  // Needs and Non-Negotiables (24-25)
  needsNonnegotiables.forEach((question) => {
    questions.push({ id: id++, categoryId: "needs-nonnegotiables", question });
  });

  return questions;
};

// Enhanced Voice Visualizer Component
const VoiceVisualizer = ({ isRecording, stream }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    if (!isRecording || !stream) return;

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    source.connect(analyser);
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.85;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      if (!isRecording) return;

      animationRef.current = requestAnimationFrame(draw);

      analyser.getByteFrequencyData(dataArray);

      // Clear with fade effect
      ctx.fillStyle = "rgba(249, 250, 251, 0.95)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 3.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = (dataArray[i] / 255) * canvas.height * 0.7;

        // Modern gradient bars
        const gradient = ctx.createLinearGradient(
          0,
          canvas.height - barHeight,
          0,
          canvas.height
        );

        const hue = (i / bufferLength) * 60 + 270; // Purple to pink gradient
        gradient.addColorStop(0, `hsla(${hue}, 70%, 60%, 0.8)`);
        gradient.addColorStop(1, `hsla(${hue}, 70%, 50%, 1)`);

        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 2, barHeight);

        x += barWidth;
      }
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContext.state !== "closed") {
        audioContext.close();
      }
    };
  }, [isRecording, stream]);

  return (
    <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50 p-1">
      <canvas
        ref={canvasRef}
        width={600}
        height={100}
        className="w-full h-24 rounded-xl"
      />
    </div>
  );
};

// Progress Ring Component
const ProgressRing = ({ progress, size = 40, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#e5e7eb"
        strokeWidth={strokeWidth}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke="#7c3aed"
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="progress-ring"
      />
    </svg>
  );
};

// Language Selection Modal Component
const LanguageSelectionModal = ({ isOpen, onSelectLanguage }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="glass rounded-3xl shadow-2xl p-8 md:p-10 max-w-md w-full mx-4 animate-fadeIn">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-medium text-gray-900 mb-2">
            {t("assessment.languageModal.title")}
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            {t("assessment.languageModal.subtitle")}
          </p>
        </div>

        <div className="flex flex-col gap-3 mb-6">
          <button
            type="button"
            onClick={() => onSelectLanguage("en")}
            className="w-full premium-button text-white rounded-xl py-3.5 font-semibold hover-lift flex items-center justify-center gap-2"
          >
            <span>{t("assessment.languageModal.english")}</span>
          </button>
          <button
            type="button"
            onClick={() => onSelectLanguage("nl")}
            className="w-full bg-white text-gray-900 rounded-xl py-3.5 font-semibold hover-lift border border-gray-200 flex items-center justify-center gap-2"
          >
            <span>{t("assessment.languageModal.dutch")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get categories and questions from translations
  const categories = getCategories(t);
  const questionsConfig = getQuestionsConfig(t);

  const [currentScreen, setCurrentScreen] = useState("welcome");
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [currentCategory, setCurrentCategory] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [savedStatus, setSavedStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [occupation, setOccupation] = useState("");
  const [currentChallenge, setCurrentChallenge] = useState("");
  const [followUpQuestion, setFollowUpQuestion] = useState("");
  const [isCheckingContext, setIsCheckingContext] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showContextSuccess, setShowContextSuccess] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [lastCheckLength, setLastCheckLength] = useState(0);
  const MAX_FOLLOWUPS = 5;
  const [hasChosenLanguage, setHasChosenLanguage] = useState(false);

  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const STORAGE_KEY = "questionnaire_answers";

  // Initialize language from localStorage
  useEffect(() => {
    try {
      const storedLang = localStorage.getItem("assessment_language");
      if (storedLang === "en" || storedLang === "nl") {
        if (i18n.language !== storedLang) {
          i18n.changeLanguage(storedLang);
        }
        setHasChosenLanguage(true);
      }
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [i18n]);

  // Keep URL path language in sync with current language
  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    const currentLang = i18n.language || "en";
    if (pathLang !== currentLang) {
      const pathWithoutLang =
        location.pathname.replace(/^\/[a-z]{2}/, "") || "/";
      const newPath =
        "/" + currentLang + (pathWithoutLang === "/" ? "" : pathWithoutLang);
      const search = location.search || "";
      navigate(newPath + search, { replace: true });
    }
  }, [location.pathname, location.search, i18n.language, navigate]);

  // Update HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Initial loading animation
  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Get email from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, []);

  // Update current category when question changes
  useEffect(() => {
    const categoryIndex = categories.findIndex(
      (cat) =>
        currentQuestion >= cat.startIndex && currentQuestion <= cat.endIndex
    );
    if (categoryIndex !== -1) {
      setCurrentCategory(categoryIndex);
    }
  }, [currentQuestion]);

  // Save to localStorage
  const saveToStorage = useCallback(
    (data) => {
      try {
        const storageData = {
          answers: data,
          firstName: firstName,
          email: email,
          dateOfBirth: dateOfBirth,
          occupation: occupation,
          currentChallenge: currentChallenge,
          currentScreen: currentScreen,
          currentQuestion: currentQuestion,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
        setSavedStatus("saved");
        setTimeout(() => setSavedStatus(""), 2000);
      } catch (error) {
        console.error("Error saving to localStorage:", error);
      }
    },
    [
      firstName,
      email,
      dateOfBirth,
      occupation,
      currentChallenge,
      currentScreen,
      currentQuestion,
    ]
  );

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedData = JSON.parse(saved);
        if (parsedData.answers && Object.keys(parsedData.answers).length > 0) {
          setAnswers(parsedData.answers);
          setFirstName(parsedData.firstName || "");
          if (!email && parsedData.email) {
            setEmail(parsedData.email);
          }
          setDateOfBirth(parsedData.dateOfBirth || "");
          setOccupation(parsedData.occupation || "");
          setCurrentChallenge(parsedData.currentChallenge || "");
          setCurrentScreen(parsedData.currentScreen || "welcome");
          setCurrentQuestion(parsedData.currentQuestion || 0);
        }
      }
    } catch (error) {
      console.error("Error loading from localStorage:", error);
    }
  }, [email]);

  // Auto-save
  useEffect(() => {
    if (
      Object.keys(answers).length > 0 ||
      firstName ||
      dateOfBirth ||
      occupation ||
      currentChallenge
    ) {
      saveToStorage(answers);
    }
  }, [
    answers,
    firstName,
    dateOfBirth,
    occupation,
    currentChallenge,
    saveToStorage,
  ]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 400) + "px";
    }
  }, [answers[currentQuestion]]);

  const checkContextAndGetFollowUp = useCallback(async (questionId, answer) => {
    // Don't check for very short answers
    if (!answer || answer.trim().length < 30) {
      setShowFollowUp(false);
      setShowContextSuccess(false);
      return;
    }

    // Don't check if we're already checking
    if (isCheckingContext) return;

    // Check if answer has changed since last check (more than 10 characters difference)
    const currentLength = answer.trim().length;
    const lengthDifference = Math.abs(currentLength - lastCheckLength);

    // Only check if there's meaningful change or it's the first check
    if (lengthDifference < 10 && lastCheckLength > 0) {
      return;
    }

    setIsCheckingContext(true);
    setLastCheckLength(currentLength);

    try {
      const response = await fetch("/api/check-context", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          questionId: questionId,
          question: questionsConfig[currentQuestion].question,
          answer: answer,
          previousAnswers: Object.entries(answers)
            .slice(0, currentQuestion)
            .map(([idx, ans]) => ({
              question: questionsConfig[idx]?.question,
              answer: ans,
            }))
            .filter((qa) => qa.answer && qa.answer.trim()),
          allQuestions: questionsConfig,
          language: i18n.language,
        }),
      });

      if (response.ok) {
        const result = await response.json();

        // Update UI based on result
        if (result.needsMoreContext && result.followUpQuestion) {
          // Only show new follow-up if it's different from current one
          if (result.followUpQuestion !== followUpQuestion) {
            setFollowUpQuestion(result.followUpQuestion);
            setShowFollowUp(true);
            setShowContextSuccess(false);
            if (!showFollowUp) {
              // Only increment if showing a new follow-up
              setFollowUpCount((prev) => prev + 1);
            }
          }
        } else if (answer.trim().length > 50) {
          // Show success message
          setShowFollowUp(false);
          setFollowUpQuestion("");
          setShowContextSuccess(true);
          // Auto-hide success message after 5 seconds
          setTimeout(() => {
            setShowContextSuccess(false);
          }, 5000);
        } else {
          // Not enough context but too short for follow-up
          setShowFollowUp(false);
          setShowContextSuccess(false);
        }
      }
    } catch (error) {
      console.error("Error checking context:", error);
    } finally {
      setIsCheckingContext(false);
    }
  }, [currentQuestion, questionsConfig, answers, i18n.language]);

  const handleInputChange = (value) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion]: value,
    }));
  };

  // Enhanced useEffect for continuous context checking with debouncing
  useEffect(() => {
    const currentAnswer = answers[currentQuestion] || "";

    // Clear any existing timeout
    const timeoutId = setTimeout(() => {
      // Always check when there's content, regardless of what was shown before
      if (currentAnswer.length > 30) {
        checkContextAndGetFollowUp(currentQuestion + 1, currentAnswer);
      } else {
        // Clear messages if answer is too short
        setShowFollowUp(false);
        setShowContextSuccess(false);
      }
    }, 2000); // Check after 2 seconds of no typing

    return () => clearTimeout(timeoutId);
  }, [answers[currentQuestion], currentQuestion, checkContextAndGetFollowUp]); // Re-run whenever answer changes

  const progress = (
    (Object.keys(answers).filter((key) => answers[key]?.trim()).length /
      questionsConfig.length) *
    100
  ).toFixed(0);

  // Get progress for current category
  const getCategoryProgress = (category) => {
    const categoryQuestions = [];
    for (let i = category.startIndex; i <= category.endIndex; i++) {
      categoryQuestions.push(i);
    }
    const answeredInCategory = categoryQuestions.filter((q) =>
      answers[q]?.trim()
    ).length;
    return (answeredInCategory / categoryQuestions.length) * 100;
  };

  const handleNext = () => {
    if (currentQuestion < questionsConfig.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowFollowUp(false);
      setShowContextSuccess(false);
      setFollowUpQuestion("");
      setLastCheckLength(0); // Reset check tracking
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowFollowUp(false);
      setShowContextSuccess(false);
      setFollowUpQuestion("");
      setLastCheckLength(0); // Reset check tracking
    }
  };

  const handleCategoryChange = (categoryIndex) => {
    const category = categories[categoryIndex];
    setCurrentQuestion(category.startIndex);
    setCurrentCategory(categoryIndex);
    setShowFollowUp(false);
    setShowContextSuccess(false);
    setFollowUpQuestion("");
    setLastCheckLength(0); // Reset check tracking
    setShowCategoryDropdown(false);
  };

  const handleStartAssessment = () => {
    setCurrentScreen("personalInfo");
  };

  const handlePersonalInfoSubmit = () => {
    if (!firstName.trim()) {
      alert(t("assessment.personalInfo.errors.firstName"));
      return;
    }
    if (!email.trim()) {
      alert(t("assessment.personalInfo.errors.email"));
      return;
    }
    if (!dateOfBirth.trim()) {
      alert(t("assessment.personalInfo.errors.dateOfBirth"));
      return;
    }
    if (!occupation.trim()) {
      alert(t("assessment.personalInfo.errors.occupation"));
      return;
    }
    if (!currentChallenge.trim()) {
      alert(t("assessment.personalInfo.errors.currentChallenge"));
      return;
    }
    setCurrentScreen("questions");
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert(t("assessment.questions.microphoneError"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result.split(",")[1]; // Remove data:audio/webm;base64, prefix
          resolve(base64String);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);

      const audioData = await base64Promise;

      const response = await fetch("/api/transcribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          audioData: audioData,
          mimeType: "audio/webm",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.text) {
        setAnswers((prev) => ({
          ...prev,
          [currentQuestion]:
            (prev[currentQuestion] || "") +
            (prev[currentQuestion] ? " " : "") +
            data.text,
        }));
      }

      setIsTranscribing(false);
    } catch (error) {
      console.error("Transcription error:", error);
      alert(t("assessment.questions.transcriptionError"));
      setIsTranscribing(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus("");

    const answeredQuestions = Object.keys(answers).filter((key) =>
      answers[key]?.trim()
    );
    if (answeredQuestions.length < questionsConfig.length) {
      setSubmitStatus(t("assessment.questions.submitError"));
      setIsSubmitting(false);
      return;
    }

    // Format data for submission - includes language for report generation
    const formattedData = {
      timestamp: new Date().toISOString(),
      firstName: firstName,
      email: email,
      dateOfBirth: dateOfBirth,
      occupation: occupation,
      currentChallenge: currentChallenge,
      language: i18n.language || "en",
      responses: questionsConfig.map((q, index) => ({
        question_id: q.id,
        question: q.question,
        answer: answers[index] || "",
      })),
    };

    try {
      const response = await fetch("/api/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formattedData),
      });

      let responseData;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.indexOf("application/json") !== -1) {
        responseData = await response.json();
      } else {
        const text = await response.text();
        console.error("Non-JSON response:", text);
        throw new Error("Server error - check Vercel logs");
      }

      if (!response.ok) {
        throw new Error(
          responseData.error || responseData.details || "Submission failed"
        );
      }

      localStorage.removeItem(STORAGE_KEY);
      const langForRedirect = i18n.language || "en";
      window.location.href = `/${langForRedirect}/done`;
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitStatus(t("assessment.questions.submitErrorGeneric"));
      setIsSubmitting(false);
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center animate-fadeIn">
          <div className="w-16 h-16 mx-auto mb-4">
            <Sparkles className="w-full h-full text-purple-600 animate-pulse" />
          </div>
          <h2 className="text-2xl font-medium gradient-text">{t("assessment.loading")}</h2>
        </div>
      </div>
    );
  }

  const params = new URLSearchParams(window.location.search);
  const emailParam = params.get("email");
  const showLanding = !emailParam;

  // Handle in-app legal pages (Terms, Privacy, Cookies)
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const langSegment = pathSegments[0];
  const sectionSegment = pathSegments[1];
  const legalSlug = pathSegments[2];

  if (sectionSegment === "legal" && (langSegment === "en" || langSegment === "nl")) {
    const renderLegalTitle = () => {
      switch (legalSlug) {
        case "terms-of-use":
          return t("footer.terms");
        case "privacy-policy":
          return t("footer.privacy");
        case "cookie-policy":
          return t("footer.cookie");
        default:
          return t("footer.terms");
      }
    };

    const renderLegalBody = () => {
      if (legalSlug === "terms-of-use") {
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-500 mb-6">{t("legal.termsOfUse.effectiveDate")}</p>
            <p className="mb-4">{t("legal.termsOfUse.intro")}</p>
            <p className="mb-6">{t("legal.termsOfUse.intro2")}</p>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section1.title")}</h2>
              <p>{t("legal.termsOfUse.section1.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section2.title")}</h2>
              <p className="mb-3">{t("legal.termsOfUse.section2.content")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t("legal.termsOfUse.section2.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section3.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section3.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section3.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section3.subtitle2")}</h3>
              <p className="mb-2">{t("legal.termsOfUse.section3.content2")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                {t("legal.termsOfUse.section3.items2", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section3.subtitle3")}</h3>
              <p className="mb-2">{t("legal.termsOfUse.section3.content3")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t("legal.termsOfUse.section3.items3", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section4.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section4.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section4.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section4.subtitle2")}</h3>
              <p className="mb-2">{t("legal.termsOfUse.section4.content2")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                {t("legal.termsOfUse.section4.items2", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section4.subtitle3")}</h3>
              <p className="mb-2">{t("legal.termsOfUse.section4.content3")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t("legal.termsOfUse.section4.items3", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section5.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section5.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section5.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section5.subtitle2")}</h3>
              <p className="mb-2">{t("legal.termsOfUse.section5.content2")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                {t("legal.termsOfUse.section5.items2", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p className="font-semibold text-gray-900 mb-4">{t("legal.termsOfUse.section5.important")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section5.subtitle3")}</h3>
              <p>{t("legal.termsOfUse.section5.content3")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section6.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section6.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section6.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section6.subtitle2")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section6.content2")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section6.subtitle3")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section6.content3")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section6.subtitle4")}</h3>
              <p>{t("legal.termsOfUse.section6.content4")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section7.title")}</h2>
              <p className="mb-3">{t("legal.termsOfUse.section7.content")}</p>
              <p className="font-medium mb-2">{t("legal.termsOfUse.section7.keyPoints")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t("legal.termsOfUse.section7.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section8.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section8.subtitle1")}</h3>
              <p className="mb-3">{t("legal.termsOfUse.section8.content1")}</p>
              <p className="mb-4">{t("legal.termsOfUse.section8.content1b")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section8.subtitle2")}</h3>
              <p className="mb-3">{t("legal.termsOfUse.section8.content2")}</p>
              <p>{t("legal.termsOfUse.section8.content2b")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section9.title")}</h2>
              <p className="mb-2">{t("legal.termsOfUse.section9.content")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t("legal.termsOfUse.section9.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section10.title")}</h2>
              <p className="mb-3">{t("legal.termsOfUse.section10.content")}</p>
              <p>{t("legal.termsOfUse.section10.content2")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section11.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section11.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section11.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section11.subtitle2")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section11.content2")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section11.subtitle3")}</h3>
              <p className="mb-2">{t("legal.termsOfUse.section11.content3")}</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                {t("legal.termsOfUse.section11.items3", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section12.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section12.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section12.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section12.subtitle2")}</h3>
              <p>{t("legal.termsOfUse.section12.content2")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section13.title")}</h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section13.subtitle1")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section13.content1")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section13.subtitle2")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section13.content2")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section13.subtitle3")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section13.content3")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section13.subtitle4")}</h3>
              <p className="mb-4">{t("legal.termsOfUse.section13.content4")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t("legal.termsOfUse.section13.subtitle5")}</h3>
              <p>{t("legal.termsOfUse.section13.content5")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">{t("legal.termsOfUse.section14.title")}</h2>
              <p className="mb-2">{t("legal.termsOfUse.section14.content")}</p>
              <p className="mb-1">{t("legal.termsOfUse.section14.company")}</p>
              <p className="mb-1">{t("legal.termsOfUse.section14.email")}</p>
              <p className="mb-6">{t("legal.termsOfUse.section14.website")}</p>

              <p className="mt-6 mb-4">{t("legal.termsOfUse.closing")}</p>
              <p className="text-sm text-gray-500">{t("legal.termsOfUse.copyright")}</p>
            </div>
          </div>
        );
      }

      if (legalSlug === "privacy-policy") {
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-500 mb-6">{t("legal.privacyPolicy.effectiveDate")}</p>
            <p className="mb-4">{t("legal.privacyPolicy.intro")}</p>
            <p className="mb-6">{t("legal.privacyPolicy.intro2")}</p>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section2.title")}
              </h2>
              <p className="mb-2">{t("legal.privacyPolicy.section2.content")}</p>
              <p className="font-medium mt-2 mb-1">{t("legal.privacyPolicy.section2.contactHeading")}</p>
              <p className="mb-1">{t("legal.privacyPolicy.section2.email")}</p>
              <p className="mb-4">{t("legal.privacyPolicy.section2.website")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section3.title")}
              </h2>
              <p className="mb-4">{t("legal.privacyPolicy.section3.intro")}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section3.sub1.title")}
              </h3>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub1.accountHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section3.sub1.accountItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub1.assessmentHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section3.sub1.assessmentItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub1.paymentHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section3.sub1.paymentItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub1.communicationHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section3.sub1.communicationItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section3.sub2.title")}
              </h3>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub2.technicalHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section3.sub2.technicalItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub2.usageHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section3.sub2.usageItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section3.sub2.marketingHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.privacyPolicy.section3.sub2.marketingItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section4.title")}
              </h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section4.sub1.title")}
              </h3>
              <p className="mb-3">{t("legal.privacyPolicy.section4.sub1.intro")}</p>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section4.sub1.provideHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section4.sub1.provideItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section4.sub1.aiHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section4.sub1.aiItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section4.sub1.supportHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section4.sub1.supportItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section4.sub1.opsHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section4.sub1.opsItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section4.sub2.title")}
              </h3>
              <p className="mb-3">{t("legal.privacyPolicy.section4.sub2.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section4.sub2.bases", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section4.sub3.title")}
              </h3>
              <p className="mb-3">{t("legal.privacyPolicy.section4.sub3.intro")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-2">
                {t("legal.privacyPolicy.section4.sub3.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="mb-6">{t("legal.privacyPolicy.section4.sub3.note")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section5.title")}
              </h2>
              <p className="mb-4">{t("legal.privacyPolicy.section5.intro")}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section5.sub1.title")}
              </h3>
              <p className="mb-2">{t("legal.privacyPolicy.section5.sub1.intro")}</p>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section5.sub1.coreHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section5.sub1.coreItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section5.sub1.analyticsHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section5.sub1.analyticsItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.privacyPolicy.section5.sub1.integrationHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section5.sub1.integrationItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section5.sub2.title")}
              </h3>
              <p className="mb-4">{t("legal.privacyPolicy.section5.sub2.content")}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section5.sub3.title")}
              </h3>
              <p className="mb-4">{t("legal.privacyPolicy.section5.sub3.content")}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section5.sub4.title")}
              </h3>
              <p className="mb-6">{t("legal.privacyPolicy.section5.sub4.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section6.title")}
              </h2>
              <p className="mb-3">{t("legal.privacyPolicy.section6.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.privacyPolicy.section6.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section7.title")}
              </h2>
              <p className="mb-3">{t("legal.privacyPolicy.section7.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.privacyPolicy.section7.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section8.title")}
              </h2>
              <p className="mb-3">{t("legal.privacyPolicy.section8.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.privacyPolicy.section8.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section9.title")}
              </h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section9.sub1.title")}
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section9.sub1.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section9.sub2.title")}
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section9.sub2.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section9.sub3.title")}
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.privacyPolicy.section9.sub3.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section9.sub4.title")}
              </h3>
              <p className="mb-6">{t("legal.privacyPolicy.section9.sub4.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section10.title")}
              </h2>
              <p className="mb-3">{t("legal.privacyPolicy.section10.content")}</p>
              <p className="font-medium mb-2">
                {t("legal.privacyPolicy.section10.usesHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.privacyPolicy.section10.usesItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section11.title")}
              </h2>
              <p className="mb-6">{t("legal.privacyPolicy.section11.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section12.title")}
              </h2>
              <p className="mb-6">{t("legal.privacyPolicy.section12.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section13.title")}
              </h2>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section13.sub1.title")}
              </h3>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.privacyPolicy.section13.sub1.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section13.sub2.title")}
              </h3>
              <p className="mb-3">{t("legal.privacyPolicy.section13.sub2.content")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section13.sub3.title")}
              </h3>
              <p className="mb-3">{t("legal.privacyPolicy.section13.sub3.content")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section13.sub4.title")}
              </h3>
              <p className="mb-3">{t("legal.privacyPolicy.section13.sub4.content")}</p>
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.privacyPolicy.section13.sub5.title")}
              </h3>
              <p className="mb-6">{t("legal.privacyPolicy.section13.sub5.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section14.title")}
              </h2>
              <p className="mb-3">{t("legal.privacyPolicy.section14.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.privacyPolicy.section14.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.privacyPolicy.section15.title")}
              </h2>
              <p className="mb-2">{t("legal.privacyPolicy.section15.content")}</p>
              <p className="mb-1">{t("legal.privacyPolicy.section15.company")}</p>
              <p className="mb-1">{t("legal.privacyPolicy.section15.email")}</p>
              <p className="mb-2">{t("legal.privacyPolicy.section15.website")}</p>
              <p className="mb-2">{t("legal.privacyPolicy.section15.euNote")}</p>
              <p className="mb-4">{t("legal.privacyPolicy.section15.authority")}</p>
              <p className="mt-4 mb-4">{t("legal.privacyPolicy.section15.closing")}</p>
            </div>
          </div>
        );
      }

      if (legalSlug === "cookie-policy") {
        return (
          <div className="space-y-6">
            <p className="text-sm text-gray-500 mb-6">{t("legal.cookiePolicy.effectiveDate")}</p>
            <p className="mb-4">{t("legal.cookiePolicy.intro")}</p>
            <p className="mb-6">{t("legal.cookiePolicy.intro2")}</p>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section2.title")}
              </h2>
              <p className="mb-3">{t("legal.cookiePolicy.section2.content")}</p>
              <p className="font-medium mb-1">
                {t("legal.cookiePolicy.section2.durationHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.cookiePolicy.section2.durationItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.cookiePolicy.section2.originHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.cookiePolicy.section2.originItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section3.title")}
              </h2>
              <p className="mb-4">{t("legal.cookiePolicy.section3.intro")}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section3.sub1.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section3.sub1.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.cookiePolicy.section3.sub1.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section3.sub2.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section3.sub2.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.cookiePolicy.section3.sub2.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section3.sub3.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section3.sub3.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.cookiePolicy.section3.sub3.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section3.sub4.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section3.sub4.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.cookiePolicy.section3.sub4.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section4.title")}
              </h2>
              <p className="mb-4">{t("legal.cookiePolicy.section4.intro")}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section4.sub1.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section4.sub1.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-2">
                {t("legal.cookiePolicy.section4.sub1.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="mb-4 text-sm text-gray-600">
                {t("legal.cookiePolicy.section4.sub1.moreInfo")}
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section4.sub2.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section4.sub2.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-2">
                {t("legal.cookiePolicy.section4.sub2.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="mb-4 text-sm text-gray-600">
                {t("legal.cookiePolicy.section4.sub2.moreInfo")}
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section4.sub3.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section4.sub3.content")}</p>
              <p className="mb-4 text-sm text-gray-600">
                {t("legal.cookiePolicy.section4.sub3.moreInfo")}
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section4.sub4.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section4.sub4.content")}</p>
              <p className="mb-6 text-sm text-gray-600">
                {t("legal.cookiePolicy.section4.sub4.moreInfo")}
              </p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section5.title")}
              </h2>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section5.sub1.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section5.sub1.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.cookiePolicy.section5.sub1.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section5.sub2.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section5.sub2.intro")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.cookiePolicy.section5.sub2.items", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>
              <p className="font-medium mb-1">
                {t("legal.cookiePolicy.section5.sub2.browserHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.cookiePolicy.section5.sub2.browserItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section5.sub3.title")}
              </h3>
              <p className="mb-2">{t("legal.cookiePolicy.section5.sub3.google")}</p>
              <p className="mb-2">{t("legal.cookiePolicy.section5.sub3.facebook")}</p>
              <p className="font-medium mb-1">
                {t("legal.cookiePolicy.section5.sub3.generalHeading")}
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-4">
                {t("legal.cookiePolicy.section5.sub3.generalItems", { returnObjects: true }).map(
                  (item, idx) => (
                    <li key={idx}>{item}</li>
                  )
                )}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">
                {t("legal.cookiePolicy.section5.sub4.title")}
              </h3>
              <p className="mb-6">{t("legal.cookiePolicy.section5.sub4.content")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section6.title")}
              </h2>
              <p className="mb-3">{t("legal.cookiePolicy.section6.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.cookiePolicy.section6.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p className="mb-6">{t("legal.cookiePolicy.section6.note")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section7.title")}
              </h2>
              <p className="mb-3">{t("legal.cookiePolicy.section7.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-6">
                {t("legal.cookiePolicy.section7.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section8.title")}
              </h2>
              <p className="mb-3">{t("legal.cookiePolicy.section8.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.cookiePolicy.section8.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p className="mb-6">{t("legal.cookiePolicy.section8.note")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section9.title")}
              </h2>
              <p className="mb-2">{t("legal.cookiePolicy.section9.content")}</p>
              <p className="mb-1">{t("legal.cookiePolicy.section9.company")}</p>
              <p className="mb-1">{t("legal.cookiePolicy.section9.email")}</p>
              <p className="mb-6">{t("legal.cookiePolicy.section9.website")}</p>

              <h2 className="text-xl font-semibold text-gray-900 mt-6 mb-2">
                {t("legal.cookiePolicy.section10.title")}
              </h2>
              <p className="mb-3">{t("legal.cookiePolicy.section10.content")}</p>
              <ul className="list-disc list-inside space-y-1 ml-4 mb-3">
                {t("legal.cookiePolicy.section10.items", { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <p className="mt-4 mb-4">{t("legal.cookiePolicy.section10.closing")}</p>
              <p className="text-sm text-gray-500">
                {t("legal.cookiePolicy.section10.copyright")}
              </p>
            </div>
          </div>
        );
      }

      // Fallback for Dutch privacy and cookie policies (keep existing simple content for now)
      const lang = i18n.language || "en";
      if (lang === "nl") {
        switch (legalSlug) {
          case "privacy-policy":
            return (
              <>
                <p className="mb-4">
                  We verwerken je gegevens uitsluitend om je persoonlijke Purpose Profile te kunnen genereren en
                  de dienst goed te laten functioneren.
                </p>
                <p className="mb-4">
                  We verkopen je gegevens niet aan derden. Alleen strikt noodzakelijke verwerkers (zoals hosting- of
                  betalingsproviders) kunnen toegang hebben, onder duidelijke afspraken.
                </p>
                <p>
                  Je kunt altijd een verzoek indienen om je gegevens in te zien of te laten verwijderen. Neem in dat
                  geval contact op via het e-mailadres dat je bij aankoop hebt gebruikt.
                </p>
              </>
            );
          case "cookie-policy":
            return (
              <>
                <p className="mb-4">
                  We gebruiken alleen cookies en vergelijkbare technieken die nodig zijn om de website en de quiz
                  goed te laten werken, en om anonieme statistieken te verzamelen.
                </p>
                <p>
                  Je kunt cookies blokkeren via je browserinstellingen. Het kan dan zijn dat bepaalde onderdelen
                  van de site minder goed werken.
                </p>
              </>
            );
          default:
            return null;
        }
      } else {
        switch (legalSlug) {
          case "cookie-policy":
            return (
              <>
                <p className="mb-4">
                  We use only those cookies and similar technologies that are necessary for the site and
                  quiz to function, and to collect anonymous usage statistics.
                </p>
                <p>
                  You can block cookies via your browser settings, but some parts of the site may then
                  function less reliably.
                </p>
              </>
            );
          default:
            return null;
        }
      }
    };

    return (
      <div className="min-h-screen gradient-bg py-12 px-4 page-transition">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(`/${i18n.language || "en"}`)}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back to home</span>
          </button>
          <div className="glass rounded-3xl shadow-2xl p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6">
              {renderLegalTitle()}
            </h1>
            <div className="text-gray-700 text-base leading-relaxed prose prose-gray max-w-none">
              {renderLegalBody()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showLanding) {
    return (
      <MyPurposeProfileLanding
        onStart={() => {
          window.history.replaceState({}, "", window.location.pathname);
          setCurrentScreen("welcome"); // or "personalInfo" if you want to skip the Loom intro
        }}
      />
    );
  }

  // Welcome Screen
  if (currentScreen === "welcome") {
    return (
      <div className="min-h-screen gradient-bg py-12 px-4 page-transition">
        <div className="max-w-5xl mx-auto">
          <LanguageSelectionModal
            isOpen={!hasChosenLanguage}
            onSelectLanguage={(lang) => {
              i18n.changeLanguage(lang);
              try {
                localStorage.setItem("assessment_language", lang);
              } catch (e) {
                // Ignore localStorage errors
              }
              setHasChosenLanguage(true);
              const pathWithoutLang =
                location.pathname.replace(/^\/[a-z]{2}/, "") || "/";
              const newPath =
                "/" + lang + (pathWithoutLang === "/" ? "" : pathWithoutLang);
              const search = location.search || "";
              navigate(newPath + search, { replace: true });
            }}
          />

          {/* Hero Section */}
          <div className="text-center mb-12 animate-fadeIn">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-6">
              <Sparkles size={16} />
              <span>{t("assessment.welcome.badge")}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="gradient-text">{t("assessment.welcome.headline1")}</span>
              <br />
              <span className="text-gray-900">{t("assessment.welcome.headline2")}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t("assessment.welcome.description")}
            </p>
          </div>

          {/* Main Card */}
          <div className="glass rounded-3xl shadow-2xl p-8 md:p-12 mb-8 card-hover">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-medium text-gray-900 mb-2">
                {t("assessment.welcome.title")}
              </h2>
              <p className="text-gray-600">
                {t("assessment.welcome.subtitle")}
              </p>
            </div>

            {/* Instruction Steps */}
            <div className="space-y-4 mb-10">
              {[
                { emoji: "🕐", text: t("assessment.welcome.step1") },
                { emoji: "✏️", text: t("assessment.welcome.step2") },
                { emoji: "🧘", text: t("assessment.welcome.step3") },
                { emoji: "🔮", text: t("assessment.welcome.step4") },
                { emoji: "📋", text: t("assessment.welcome.step5") },
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-4 p-4 rounded-xl bg-purple-50">
                  <span className="text-2xl">{step.emoji}</span>
                  <p className="text-gray-700">{step.text}</p>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button
              onClick={handleStartAssessment}
              className="w-full premium-button text-white rounded-2xl py-4 px-8 font-semibold text-lg hover-lift flex items-center justify-center gap-3"
            >
              <span>{t("assessment.welcome.startButton")}</span>
              <ArrowRight size={20} />
            </button>

            {/* Legal Links */}
            <p className="text-center text-xs text-gray-500 mt-6">
              {t("assessment.welcome.legalText")}{" "}
              <a
                href={`/${i18n.language || "en"}/legal/terms-of-use`}
                className="text-purple-600 hover:text-purple-800 underline"
              >
                {t("assessment.welcome.terms")}
              </a>
              ,{" "}
              <a
                href={`/${i18n.language || "en"}/legal/privacy-policy`}
                className="text-purple-600 hover:text-purple-800 underline"
              >
                {t("assessment.welcome.privacy")}
              </a>
              , and{" "}
              <a
                href={`/${i18n.language || "en"}/legal/cookie-policy`}
                className="text-purple-600 hover:text-purple-800 underline"
              >
                {t("assessment.welcome.cookie")}
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Personal Info Screen
  if (currentScreen === "personalInfo") {
    return (
      <div className="min-h-screen gradient-bg py-12 px-4 page-transition">
        <div className="max-w-xl mx-auto">
          <div className="glass rounded-3xl shadow-2xl p-8 md:p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-2xl flex items-center justify-center">
                <User className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-3xl font-medium text-gray-900 mb-2">
                {t("assessment.personalInfo.title")}
              </h2>
              <p className="text-gray-600">
                {t("assessment.personalInfo.subtitle")}
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("assessment.personalInfo.firstName")}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t("assessment.personalInfo.firstNamePlaceholder")}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 transition-all text-lg"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("assessment.personalInfo.email")}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl text-gray-600 cursor-not-allowed text-lg pl-12"
                  />
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t("assessment.personalInfo.emailNote")}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("assessment.personalInfo.dateOfBirth")}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 transition-all text-lg pl-12"
                  />
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("assessment.personalInfo.occupation")}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={occupation}
                    onChange={(e) => setOccupation(e.target.value)}
                    placeholder={t("assessment.personalInfo.occupationPlaceholder")}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 transition-all text-lg pl-12"
                  />
                  <Briefcase className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("assessment.personalInfo.currentChallenge")}
                </label>
                <div className="relative">
                  <textarea
                    value={currentChallenge}
                    onChange={(e) => setCurrentChallenge(e.target.value)}
                    placeholder={t("assessment.personalInfo.currentChallengePlaceholder")}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 transition-all text-lg resize-none pl-12 pt-4"
                    rows={4}
                  />
                  <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t("assessment.personalInfo.currentChallengeNote")}
                </p>
              </div>
            </div>

            <button
              onClick={handlePersonalInfoSubmit}
              disabled={
                !firstName.trim() ||
                !email.trim() ||
                !dateOfBirth.trim() ||
                !occupation.trim() ||
                !currentChallenge.trim()
              }
              className="w-full mt-8 premium-button text-white rounded-xl py-4 px-8 font-semibold text-lg hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <span>{t("assessment.personalInfo.continueButton")}</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Questions Screen
  return (
    <div className="min-h-screen gradient-bg py-6 px-4 page-transition">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 animate-fadeIn">
          <div className="text-center mb-4">
            <h1 className="text-2xl md:text-3xl font-medium text-gray-900 mb-1">
              {t("assessment.questions.greeting", { name: firstName })}
            </h1>
            <p className="text-gray-600 text-sm md:text-base">
              {t("assessment.questions.subtitle")}
            </p>
          </div>

          {/* Overall Progress */}
          <div className="glass rounded-2xl p-4 subtle-shadow">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <ProgressRing progress={parseInt(progress)} />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {progress}% {t("assessment.questions.complete")}
                  </p>
                  <p className="text-xs text-gray-600">
                    {
                      Object.keys(answers).filter((key) => answers[key]?.trim())
                        .length
                    }{" "}
                    {t("assessment.questions.of")} {questionsConfig.length} {t("assessment.questions.questionsAnswered")}
                  </p>
                </div>
              </div>

              {/* Test Mode & Status */}
              <div className="flex items-center gap-3">
                {savedStatus && (
                  <div className="flex items-center gap-1.5 text-green-600 text-sm animate-fadeIn">
                    <CheckCircle size={14} />
                    <span>{t("assessment.questions.saved")}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category Selector - Modern Dropdown Style */}
        <div className="mb-6 animate-slideUp">
          <div className="glass rounded-2xl p-1 subtle-shadow">
            <button
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 rounded-xl transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  {(() => {
                    const Icon = categories[currentCategory].icon;
                    return <Icon size={20} className="text-purple-600" />;
                  })()}
                </div>
                <div className="text-left">
                  <p className="text-xs text-gray-500 font-medium">
                    {t("assessment.questions.currentCategory")}
                  </p>
                  <p className="font-semibold text-gray-900">
                    {categories[currentCategory].name}
                  </p>
                </div>
              </div>
              <ChevronDown
                className={`text-gray-400 transform transition-transform ${
                  showCategoryDropdown ? "rotate-180" : ""
                }`}
                size={20}
              />
            </button>

            {/* Dropdown */}
            {showCategoryDropdown && (
              <div className="p-2 space-y-1 animate-fadeIn">
                {categories.map((category, index) => {
                  const Icon = category.icon;
                  const categoryProgress = getCategoryProgress(category);
                  const isActive = currentCategory === index;
                  const isComplete = categoryProgress === 100;

                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(index)}
                      className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all ${
                        isActive
                          ? "bg-purple-50 border-2 border-purple-200"
                          : "hover:bg-gray-50 border-2 border-transparent"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg ${
                          isActive
                            ? "bg-purple-600"
                            : isComplete
                            ? "bg-green-100"
                            : "bg-gray-100"
                        }`}
                      >
                        <Icon
                          size={18}
                          className={
                            isActive
                              ? "text-white"
                              : isComplete
                              ? "text-green-600"
                              : "text-gray-600"
                          }
                        />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-gray-900">
                          {category.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {category.description}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-gray-600">
                          {Math.round(categoryProgress)}%
                        </p>
                        <p className="text-xs text-gray-500">
                          {category.endIndex - category.startIndex + 1}{" "}
                          {t("assessment.questions.questions")}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Question Card */}
        <div className="glass rounded-3xl shadow-xl p-6 md:p-8 mb-6 animate-slideUp mobile-full-width">
          {/* Question Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-purple-600">
                {t("assessment.questions.question")}{" "}
                {currentQuestion - categories[currentCategory].startIndex + 1}{" "}
                {t("assessment.questions.of")}{" "}
                {categories[currentCategory].endIndex -
                  categories[currentCategory].startIndex +
                  1}
              </p>

              {/* Question Navigation Pills */}
              <div className="flex gap-1.5">
                {(() => {
                  const currentCat = categories[currentCategory];
                  const questions = [];
                  for (
                    let i = currentCat.startIndex;
                    i <= currentCat.endIndex;
                    i++
                  ) {
                    questions.push(i);
                  }
                  return questions.map((index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentQuestion(index);
                        setShowFollowUp(false);
                        setShowContextSuccess(false);
                        setFollowUpQuestion("");
                        setLastCheckLength(0); // Reset check tracking
                      }}
                      className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                        currentQuestion === index
                          ? "bg-purple-600 text-white shadow-md"
                          : answers[index]?.trim()
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {index - currentCat.startIndex + 1}
                    </button>
                  ));
                })()}
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed">
              {questionsConfig[currentQuestion].question}
            </h2>
          </div>

          <div className="divider mb-6"></div>

          {/* Answer Input */}
          <div className="mb-6">
            <textarea
              ref={textareaRef}
              value={answers[currentQuestion] || ""}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder={t("assessment.questions.placeholder")}
              className={`w-full min-h-[160px] p-4 border-2 rounded-2xl focus:border-purple-500 resize-none transition-all text-gray-800 leading-relaxed placeholder-gray-400 ${
                isCheckingContext ? "analyzing-border" : "border-gray-200"
              }`}
              style={{ fontSize: "16px" }}
            />

            {/* AI Analyzing Indicator - Enhanced */}
            {isCheckingContext && (
              <div className="flex items-center gap-2 text-purple-600 text-sm mt-2 animate-fadeIn opacity-70">
                <div className="relative">
                  <Sparkles className="w-4 h-4" />
                  <div className="absolute inset-0 animate-ping">
                    <Sparkles className="w-4 h-4 opacity-30" />
                  </div>
                </div>
                <span className="text-xs">{t("assessment.questions.listening")}</span>
              </div>
            )}
          </div>

          {/* AI Follow-up - Enhanced UI */}
          {showFollowUp && followUpQuestion && (
            <div className="mb-6 animate-fadeIn">
              <div className="p-5 glass-light rounded-2xl border border-purple-100 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm flex-shrink-0">
                    <Sparkles size={20} className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-semibold text-purple-900">
                        {t("assessment.questions.goingDeeper")}
                      </p>
                      <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                        {t("assessment.questions.optional")}
                      </span>
                    </div>
                    <p className="text-purple-800 leading-relaxed">
                      {followUpQuestion}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Context Success Message - NEW COMPONENT */}
          {showContextSuccess && !showFollowUp && (
            <div className="mb-6 animate-fadeIn">
              <div className="p-5 glass-light rounded-2xl border border-green-100 shadow-sm bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-start gap-3">
                  <div className="p-2.5 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl shadow-sm flex-shrink-0">
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-semibold text-green-900">
                        {t("assessment.questions.greatDetail")}
                      </p>
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        {t("assessment.questions.completeBadge")}
                      </span>
                    </div>
                    <p className="text-green-800 leading-relaxed">
                      {t("assessment.questions.successMessage")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Voice Recording */}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row items-center gap-3">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isTranscribing}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all hover-lift ${
                  isRecording
                    ? "bg-red-500 text-white hover:bg-red-600 breathing"
                    : "bg-purple-600 text-white hover:bg-purple-700"
                } ${isTranscribing ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isRecording ? (
                  <>
                    <MicOff size={18} />
                    <span>{t("assessment.questions.stopRecording")}</span>
                  </>
                ) : (
                  <>
                    <Mic size={18} />
                    <span>{t("assessment.questions.recordAnswer")}</span>
                  </>
                )}
              </button>

              {isTranscribing && (
                <div className="flex items-center gap-2 text-purple-600 animate-fadeIn">
                  <Loader2 className="animate-spin" size={18} />
                  <span className="text-sm font-medium">{t("assessment.questions.transcribing")}</span>
                </div>
              )}
            </div>

            {/* Voice Visualizer */}
            {isRecording && audioStream && (
              <div className="animate-fadeIn">
                <VoiceVisualizer
                  isRecording={isRecording}
                  stream={audioStream}
                />
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="flex items-center gap-2 px-5 py-2.5 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-medium"
            >
              <ChevronLeft size={18} />
              <span>{t("assessment.questions.previous")}</span>
            </button>

            <button
              onClick={handleNext}
              disabled={currentQuestion === questionsConfig.length - 1}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all font-medium hover-lift"
            >
              <span>{t("assessment.questions.next")}</span>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Submit Section */}
        {currentQuestion === questionsConfig.length - 1 && (
          <div className="glass rounded-3xl shadow-xl p-6 md:p-8 animate-fadeIn mobile-full-width">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-2xl flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-2">
                {t("assessment.questions.allDone")}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {t("assessment.questions.reviewText")}
              </p>
            </div>

            <button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                Object.keys(answers).filter((key) => answers[key]?.trim())
                  .length < questionsConfig.length
              }
              className="w-full premium-button text-white rounded-xl py-4 px-8 font-semibold text-lg hover-lift disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>{t("assessment.questions.submitting")}</span>
                </>
              ) : (
                <>
                  <Send size={20} />
                  <span>{t("assessment.questions.submitAssessment")}</span>
                </>
              )}
            </button>

            {submitStatus && (
              <div
                className={`mt-4 p-4 rounded-xl text-center font-medium animate-fadeIn ${
                  submitStatus.includes("Success")
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {submitStatus}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Routes>
      <Route path="/done" element={<DonePage />} />
      <Route path="/:lang/done" element={<DonePage />} />
      <Route path="/" element={<AppContent />} />
      <Route path="/en/*" element={<AppContent />} />
      <Route path="/nl/*" element={<AppContent />} />
    </Routes>
  );
};

export default App;
